import { StateCreator } from "zustand";

/**
 * Backend session lifetime. Used as a fallback for tokens that do not carry a
 * readable `exp` claim, so a token persisted in localStorage cannot outlive the
 * session it represents and start 401-ing on every screen the user opens.
 */
export const AUTH_TOKEN_TTL_MS = 72 * 60 * 60 * 1000;

const decodeBase64Url = (value: string): string | undefined => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "="
  );

  try {
    if (typeof atob === "function") {
      return atob(padded);
    }

    // Node / SSR (breward-lp renders on the server).
    return Buffer.from(padded, "base64").toString("binary");
  } catch {
    return undefined;
  }
};

/** Expiry in milliseconds from a JWT's `exp` claim, when the token is a JWT. */
export const readTokenExpiry = (token: string): number | undefined => {
  const payload = token.split(".")[1];
  if (!payload) {
    return undefined;
  }

  const decoded = decodeBase64Url(payload);
  if (!decoded) {
    return undefined;
  }

  try {
    const claims = JSON.parse(decoded);
    return typeof claims?.exp === "number" ? claims.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

export const isAuthTokenExpired = (
  token?: string,
  issuedAt?: number,
  rememberMe?: boolean
): boolean => {
  if (!token) {
    return false;
  }

  const expiresAt = readTokenExpiry(token);
  if (expiresAt !== undefined) {
    return Date.now() >= expiresAt;
  }

  // No readable expiry and no known issue time - leave it alone and let the API
  // decide; the 401 handler still covers that case.
  if (!issuedAt) {
    return false;
  }

  // "Remember me" asks the backend for a session longer than the default, and we
  // have no way of knowing how much longer. Never prune those locally.
  if (rememberMe) {
    return false;
  }

  return Date.now() - issuedAt >= AUTH_TOKEN_TTL_MS;
};

export interface AuthSlice {
  authToken?: string;
  authTokenIssuedAt?: number;
  authTokenRememberMe?: boolean;
  setAuthToken?: (token?: string, rememberMe?: boolean) => void;
  backToUrl?: string;
  setBackToUrl?: (url?: string) => void;
  logoutUser?: (currentUrl?: string) => void;
}

export const createAuthSlice: StateCreator<AuthSlice> = (set) => ({
  authToken: undefined,
  authTokenIssuedAt: undefined,
  authTokenRememberMe: undefined,
  backToUrl: undefined,
  setAuthToken: (token?: string, rememberMe?: boolean) =>
    set({
      authToken: token,
      authTokenIssuedAt: token ? Date.now() : undefined,
      authTokenRememberMe: token ? Boolean(rememberMe) : undefined,
    }),
  setBackToUrl: (url?: string) => set({ backToUrl: url }),
  logoutUser: (currentUrl?: string) =>
    set({
      backToUrl: currentUrl,
      authToken: undefined,
      authTokenIssuedAt: undefined,
      authTokenRememberMe: undefined,
    }),
});
